import Ably from 'ably';
import { apiKey } from '../../networking/urlEndPoints';

const ABLY_TRANSPORT_PREFERENCE_KEY = 'ably-transport-preference';

const clearPersistedTransportPreference = () => {
	if (typeof window === 'undefined') return;

	try {
		window.localStorage?.removeItem(ABLY_TRANSPORT_PREFERENCE_KEY);
	} catch (error) {
		// Ignore storage access errors in restricted browser contexts.
	}
};

const getActiveTransportName = (ablyInstance) =>
	ablyInstance?.connection?.connectionManager?.activeProtocol?.getTransport?.()?.shortName
		|| ablyInstance?.connection?.connectionManager?.activeProtocol?.transport?.shortName
		|| null;

export const getAblyRealtimeOptions = () => {
	const realtimeOptions = {
		key: apiKey,
		autoConnect: false,
		disconnectedRetryTimeout: 5000,
		suspendedRetryTimeout: 15000,
	};

	if (process.env.REACT_APP_ABLY_FORCE_FALLBACK === 'true') {
		realtimeOptions.transports = ['xhr_streaming', 'xhr_polling'];
	} else {
		// This repo still uses ably-js v1.x, whose browser client starts with HTTP
		// transports by default and upgrades later. Force websocket-only to avoid
		// the persistent recv/send long-poll traffic seen in DevTools.
		realtimeOptions.transports = ['web_socket'];
		clearPersistedTransportPreference();
	}

	return realtimeOptions;
};

class AblyConnectionManager {
	constructor() {
		this.ably = null;
		this.connectionState = 'disconnected';
		this.subscriptions = new Map(); // Track active subscriptions for cleanup
		this.isConnecting = false;
		this.connectionPromise = null; // Track connection promise to prevent duplicates
		this.missingKeyLogged = false;
		this.debugEnabled = process.env.REACT_APP_DEBUG_ABLY === 'true';
		this.isRecoveringConnection = false;
		this.isClosingIntentionally = false;
	}

	getConnection() {
		if (!apiKey) {
			if (!this.missingKeyLogged) {
				console.warn('Ably API key is not configured. Realtime subscriptions are disabled.');
				this.missingKeyLogged = true;
			}
			return null;
		}

		// Return existing connection if available and in a valid state
		if (this.ably) {
			const state = this.ably.connection.state;
			if (state === 'connected' || state === 'connecting' || state === 'initialized') {
				return this.ably;
			}
			// If connection is in a bad state, clean it up but don't create new one immediately
			if (state === 'failed' || state === 'suspended' || state === 'closed') {
				console.warn('Ably connection in bad state, cleaning up:', state);
				this.cleanupConnection({ preserveSubscriptions: true });
			}
		}

		// If already connecting, return the existing connection (even if not fully connected yet)
		if (this.isConnecting && this.ably) {
			return this.ably;
		}

		// Create new connection only if not already connecting and no connection exists
		if (!this.isConnecting && !this.ably) {
			this.isConnecting = true;
			try {
				const realtimeOptions = getAblyRealtimeOptions();
				this.ably = new Ably.Realtime(realtimeOptions);

				// Add error handling
				this.ably.connection.on('error', (error) => {
					if (this.debugEnabled) {
						console.warn('Ably connection error:', error, {
							activeTransport: getActiveTransportName(this.ably),
						});
					}
					this.connectionState = 'error';
					this.isConnecting = false;
				});

				this.ably.connection.on('connectionstatechange', (stateChange) => {
					const previousState = stateChange.previous;
					const currentState = stateChange.current;
					
					if (this.debugEnabled) {
						console.info(`Ably connection state: ${previousState} -> ${currentState}`);
					}
					
					this.connectionState = currentState;
					
					if (currentState === 'connected') {
						this.isConnecting = false;
						this.resubscribeActiveSubscriptions();
						if (this.debugEnabled) {
							console.info('Ably connection established successfully', {
								activeTransport: getActiveTransportName(this.ably),
							});
						}
					}
					if (currentState === 'failed' || currentState === 'suspended') {
						if (this.debugEnabled) {
							console.warn('Ably connection state changed:', currentState);
						}
						this.isConnecting = false;
						this.recoverConnection();
					}
					if (currentState === 'closed') {
						this.isConnecting = false;
						if (!this.isClosingIntentionally) {
							this.recoverConnection();
						}
					}
				});

				// Connect only once
				this.ably.connect();
			} catch (error) {
				if (this.debugEnabled) {
					console.error('Error creating Ably connection:', error);
				}
				this.isConnecting = false;
				this.ably = null;
				return null;
			}
		}

		return this.ably;
	}

	recoverConnection() {
		if (this.isRecoveringConnection || this.subscriptions.size === 0) return;

		this.isRecoveringConnection = true;
		window.setTimeout(() => {
			try {
				if (this.ably && ['connected', 'connecting', 'initialized'].includes(this.ably.connection.state)) {
					return;
				}

				this.cleanupConnection({ preserveSubscriptions: true });
				this.getConnection();
			} finally {
				this.isRecoveringConnection = false;
			}
		}, 1000);
	}

	resubscribeActiveSubscriptions() {
		if (!this.ably || this.subscriptions.size === 0) return;

		this.subscriptions.forEach((subscription) => {
			if (subscription.connection === this.ably) return;

			try {
				const channel = this.ably.channels.get(subscription.channelName);
				channel.subscribe(subscription.eventName, subscription.callback);
				subscription.channel = channel;
				subscription.connection = this.ably;
			} catch (error) {
				console.error('Error restoring Ably subscription:', error);
			}
		});
	}

	cleanupConnection({ preserveSubscriptions = false } = {}) {
		if (this.ably) {
			if (!preserveSubscriptions) {
				// Unsubscribe all
				this.subscriptions.forEach((_, id) => {
					try {
						this.unsubscribe(id);
					} catch (error) {
						// Ignore errors during cleanup
					}
				});
			}
			
			try {
				const state = this.ably.connection.state;
				if (state === 'connected' || state === 'connecting') {
					this.isClosingIntentionally = true;
					this.ably.close();
				}
			} catch (error) {
				console.error('Error closing Ably connection:', error);
			} finally {
				this.isClosingIntentionally = false;
			}
			
			this.ably = null;
			this.connectionState = 'disconnected';
			this.isConnecting = false;
		}
	}

	getChannel(channelName) {
		const ably = this.getConnection();
		if (!ably) return null;
		return ably.channels.get(channelName);
	}

	subscribe(channelName, eventName, callback, subscriptionId) {
		const channel = this.getChannel(channelName);
		if (!channel) return null;

		// Unsubscribe if already subscribed with same ID
		if (this.subscriptions.has(subscriptionId)) {
			this.unsubscribe(subscriptionId);
		}

		channel.subscribe(eventName, callback);
		this.subscriptions.set(subscriptionId, {
			channel,
			channelName,
			eventName,
			callback,
			connection: this.ably,
		});

		return { channel, eventName, subscriptionId };
	}

	unsubscribe(subscriptionId) {
		const subscription = this.subscriptions.get(subscriptionId);
		if (subscription) {
			try {
				subscription.channel.unsubscribe(subscription.eventName, subscription.callback);
			} catch (error) {
				console.error('Error unsubscribing:', error);
			}
			this.subscriptions.delete(subscriptionId);
		}
	}

	close() {
		this.cleanupConnection({ preserveSubscriptions: false });
	}

	isConnected() {
		return this.ably && this.connectionState === 'connected';
	}
}

// Export singleton instance
export const ablyConnectionManager = new AblyConnectionManager();
